import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import { stringify } from 'qs';
import config from '../Config';

const baseURL = config.apiUrl || __API_URL__;
const apiTimeout = 10000;
const pollApiTimeout = 180000;
const SSO_DOMAINS = ['.telia.ee', '.super.ee', '.teliatv.ee', '.diil.ee', '.coltat.teliasonera.com', '.dst.telia.com'];

const paramsSerializer = { serialize: (params: any) => stringify(params, { arrayFormat: 'repeat' }) };

const apiConfig: AxiosRequestConfig = {
  baseURL,
  paramsSerializer,
  timeout: apiTimeout,
  withCredentials: true,
};

const pollApiConfig: AxiosRequestConfig = {
  baseURL,
  paramsSerializer,
  timeout: pollApiTimeout,
  withCredentials: true,
};

const resolveBaseUrl = (defaultUrl?: string): string => {
  const isLocalPath = !defaultUrl?.startsWith('http');
  return defaultUrl && !isLocalPath ? defaultUrl : window.location.hostname;
};

const resolveDomain = (baseUrl: string): string => {
  const domain = SSO_DOMAINS.find((domain) => baseUrl.includes(domain));
  return domain || '.telia.ee';
};

const resolveEnv = (baseUrl: string): string => {
  let env = config.env;
  if (env === 'dev2') {
    env = 'dev';
  }
  if (!env) {
    if (['localhost', 'sso-local'].some((env) => baseUrl.includes(env))) {
      return 'local';
    }
    if (['arendus', 'dev', 'localhost', 'sso-local', 'ng-pood'].some((env) => baseUrl.includes(env))) {
      return 'dev';
    }
    if (['test', 'coltat'].some((env) => baseUrl.includes(env))) {
      return 'test';
    }
  }
  return env || 'live';
};

const resolveApigeeApiUrl = (defaultUrl?: string): string => {
  const baseUrl = resolveBaseUrl(defaultUrl);
  const domain = resolveDomain(baseUrl);
  const env = resolveEnv(baseUrl);
  if (env === 'local') {
    return 'https://dev.teliaapis.telia.ee/customer/sso-api/api';
  }
  if (['dev', 'test'].includes(env)) {
    return `https://${env}.teliaapis${domain}/customer/sso-api/api`;
  }
  return `https://apis${domain}/customer/sso-api/api`;
};

export const resolveSsoOrigin = (): string => {
  if (window.location !== window.parent.location) {
    return window.location.ancestorOrigins?.[0] || document.referrer;
  }
  return `${window.location.origin}${window.location.pathname}`;
};

const ssoOriginInterceptor = (config: InternalAxiosRequestConfig) => {
  const ssoOrigin = resolveSsoOrigin();
  if (ssoOrigin) {
    config.headers['X-Sso-Origin'] = ssoOrigin;
  }
  return config;
};

const APIGEE_URL = resolveApigeeApiUrl(apiConfig.baseURL);
let API = axios.create({ ...apiConfig, baseURL: APIGEE_URL, withCredentials: true });
let POLL_API = axios.create({ ...pollApiConfig, baseURL: APIGEE_URL, withCredentials: true });
API.interceptors.request.use(ssoOriginInterceptor);
POLL_API.interceptors.request.use(ssoOriginInterceptor);

const reInit = (baseUrl: string) => {
  API = axios.create({ ...apiConfig, baseURL: resolveApigeeApiUrl(baseUrl), withCredentials: true });
  POLL_API = axios.create({ ...pollApiConfig, baseURL: resolveApigeeApiUrl(baseUrl), withCredentials: true });
  API.interceptors.request.use(ssoOriginInterceptor);
  POLL_API.interceptors.request.use(ssoOriginInterceptor);
};

enum FetchStatus {
  FETCHING = 'FETCHING',
  FETCHING_MORE = 'FETCHING_MORE',
  ERROR = 'ERROR',
  FETCHED = 'FETCHED',
  UNINITIALIZED = 'UNINITIALIZED',
  NOT_FOUND = 'NOT_FOUND',
}

enum PasskeyErrorType {
  USER_CANCELLED = 'USER_CANCELLED',
  GENERAL = 'GENERAL',
}

export interface ErrorResponse {
  errorMessage?: string;
  errorCode?: string;
  softProfile?: boolean;
}

export default API;
export { API, POLL_API, FetchStatus, PasskeyErrorType, resolveApigeeApiUrl, reInit, ssoOriginInterceptor };
